/**
 * Deterministic 6-letter reference codes for the customer names and city/state
 * locations shown across the Western Express prototype.
 *
 * Two flavors, mirroring how dispatchers shorthand the two kinds of value:
 *   - locations  → first four letters of the city + the 2-letter state, e.g.
 *     "Stamford, Connecticut" → STAMCT, "Champlain PA" → CHAMPA.
 *   - companies  → six letters spread across the meaningful words, e.g.
 *     "Kroger Foods" → KROFOO, "Nestle" → NESTLE.
 *
 * Demo-only mnemonics — not a real system identifier.
 */

const STATE_ABBREVS: ReadonlySet<string> = new Set([
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "DC",
]);

const STATE_NAME_TO_ABBREV: Readonly<Record<string, string>> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
  "district of columbia": "DC",
};

function lettersOnly(value: string): string {
  return value.replace(/[^a-z]/gi, "");
}

/** Resolve a "PA" abbreviation or "Pennsylvania" full name to its postal code. */
function resolveState(raw: string): string | null {
  const cleaned = raw.trim().replace(/\.$/, "");
  const upper = cleaned.toUpperCase();
  if (upper.length === 2 && STATE_ABBREVS.has(upper)) return upper;
  return STATE_NAME_TO_ABBREV[cleaned.toLowerCase()] ?? null;
}

/** Split a location into its city text and resolved state, or null when there is no state. */
function splitCityState(value: string): { city: string; state: string } | null {
  const trimmed = value.trim();
  const commaIdx = trimmed.lastIndexOf(",");
  if (commaIdx >= 0) {
    const state = resolveState(trimmed.slice(commaIdx + 1));
    return state ? { city: trimmed.slice(0, commaIdx), state } : null;
  }
  const tokens = trimmed.split(/\s+/);
  if (tokens.length >= 2) {
    const state = resolveState(tokens[tokens.length - 1]);
    if (state) return { city: tokens.slice(0, -1).join(" "), state };
  }
  return null;
}

/**
 * The city letters for a location code: drops a leading highway/route prefix so
 * "I-40 near Jackson" yields "Jackson", while a plain "Oklahoma City" is kept whole.
 */
function cityStem(city: string): string {
  const tokens = city.trim().split(/\s+/);
  let lastPrefixIdx = -1;
  tokens.forEach((token, i) => {
    if (/\d/.test(token) || /^(near|on|at|by|mm)$/i.test(token)) lastPrefixIdx = i;
  });
  const cityTokens = lastPrefixIdx >= 0 ? tokens.slice(lastPrefixIdx + 1) : tokens;
  return lettersOnly(cityTokens.join(""));
}

/**
 * 6-letter location code: first four letters of the city plus the 2-letter
 * state. Returns null when the value has no recognizable state.
 *
 * @param value - A location string such as "Memphis, TN" or "I-40 near Jackson, TN".
 */
export function cityStateCode(value: string | null | undefined): string | null {
  if (!value) return null;
  const parsed = splitCityState(value);
  if (!parsed) return null;
  const stem = cityStem(parsed.city);
  if (stem.length === 0) return null;
  return (stem.slice(0, 4) + parsed.state).toUpperCase();
}

/** Short codes and numeric suffixes ("DC", "7037", "MEM1") aren't part of the brand. */
function isNoiseToken(token: string): boolean {
  return /\d/.test(token) || lettersOnly(token).length <= 2;
}

/**
 * 6-letter company code: six letters spread evenly across the meaningful words,
 * borrowing from longer words when a word runs short ("Kroger DC" → KROGDC).
 *
 * @param value - A company name such as "Kroger Foods" or "Nestle".
 */
export function companyCode(value: string | null | undefined): string | null {
  if (!value) return null;
  const allWords = value
    .trim()
    .split(/\s+/)
    .map(lettersOnly)
    .filter((w) => w.length > 0);
  if (allWords.length === 0) return null;
  const meaningful = value
    .trim()
    .split(/\s+/)
    .filter((token) => !isNoiseToken(token))
    .map(lettersOnly);
  const words = meaningful.length > 0 ? meaningful : allWords;

  // Round-robin one letter at a time across the words so six letters split as
  // evenly as possible (3+3, 2+2+2, …), skipping words that run out of letters.
  const take = new Array<number>(words.length).fill(0);
  let total = 0;
  let progressed = true;
  while (total < 6 && progressed) {
    progressed = false;
    for (let i = 0; i < words.length && total < 6; i++) {
      if (take[i] < words[i].length) {
        take[i]++;
        total++;
        progressed = true;
      }
    }
  }
  return words
    .map((word, i) => word.slice(0, take[i]))
    .join("")
    .toUpperCase();
}
